import {Component, Inject, signal} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {take} from 'rxjs';
import {CustomValidator} from 'src/app/modules/common';
import {ReferenceService} from '../../../services/reference.service';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {ApplicationContactDTO} from '../../../models/application/ApplicationContactDTO';
import {ReferenceLookupValue} from '../../../../clm/types/reference-lookup-value.enum';

enum Controls {
  ContactTypeCode = 'contactTypeCode',
  ContactValue = 'contactValue',
  IsPreferred = 'isPreferred'
}

@Component({
  selector: 'app-application-contact-dialog',
  templateUrl: './application-contact-dialog.component.html',
  styleUrls: [
    '../../../../../../styles/global/_dialog-styles.scss',
  ]
})
export class ApplicationContactDialogComponent {
  readonly controls = Controls;

  heading: string;
  valueHeading: string;
  valueHint = signal<string>('');
  showValueField = false;
  contactForm: FormGroup;
  existingContactDetail: ApplicationContactDTO;
  contactTypeCodes: ReferenceDTO[];

  constructor(
    private formBuilder: FormBuilder,
    private referenceService: ReferenceService,
    public dialogRef: MatDialogRef<ApplicationContactDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      applicationPersonId: number,
      contactDetail?: ApplicationContactDTO
    }
  ) {
    this.existingContactDetail = data.contactDetail;
    this.heading = this.existingContactDetail ? 'Edit Contact Details' : 'Add Contact Details';
    this.initForm();
    this.initLookUpValues();
    this.setFormValues(this.existingContactDetail);
  }

  initForm(): void {
    this.contactForm = this.formBuilder.group({
      [Controls.ContactTypeCode]: [null, Validators.required],
      [Controls.ContactValue]: ['', Validators.required],
      [Controls.IsPreferred]: [false]
    });
  }

  setFormValues(contactDetail: ApplicationContactDTO): void {
    if (contactDetail) {
      this.contactForm.patchValue({
        [Controls.ContactTypeCode]: contactDetail.contactTypeCode,
        [Controls.ContactValue]: contactDetail.contactValue,
        [Controls.IsPreferred]: contactDetail.isPreferred
      });
    }
  }

  initLookUpValues(): void {
    this.referenceService.getAllByFieldName(ReferenceLookupValue.ContactTypeCode)
      .pipe(take(1))
      .subscribe({
        next: (results: ReferenceDTO[]) => {
          this.contactTypeCodes = results;
          const formValue = this.contactForm.value;
          this.onContactTypeCodeChanged(formValue[Controls.ContactTypeCode]);
        },
      });
  }

  onContactTypeCodeChanged(contactTypeCode: string): void {
    if (this.contactTypeCodes && contactTypeCode) {
      const selectedContactType = this.contactTypeCodes.find(contactType => contactType.key === contactTypeCode);
      if (selectedContactType) {
        this.setValueFields(true, selectedContactType.value.toUpperCase().includes('PHONE'));
      }
      else {
        this.setValueFields(false);
      }
    }
    else {
      this.setValueFields(false);
    }
  }

  setValueFields(shouldShow: boolean, isNumber?: boolean): void {
    this.showValueField = shouldShow;
    const contactValueControl = this.contactForm.get(Controls.ContactValue);

    if (!shouldShow) {
      this.valueHint.set('');
      contactValueControl.clearValidators();
    } else if (isNumber) {
      this.valueHeading = 'Phone Number';
      this.valueHint.set('e.g. 0821234567');
      contactValueControl.setValidators([Validators.required, CustomValidator.mobileNumberValidator]);
    } else {
      this.valueHeading = 'Email Address';
      this.valueHint.set('example@mail.com');
      contactValueControl.setValidators([Validators.required, CustomValidator.email]);
    }

    contactValueControl.updateValueAndValidity();
  }

  onSave(): void {
    if (this.contactForm.valid) {
      const formValue = this.contactForm.value;
      const newContactDetail: ApplicationContactDTO = new ApplicationContactDTO();
      newContactDetail.contactTypeCode = formValue[Controls.ContactTypeCode];
      newContactDetail.contactValue = formValue[Controls.ContactValue];
      newContactDetail.isPreferred = formValue[Controls.IsPreferred];
      if (this.existingContactDetail) {
        newContactDetail.applicationContactId = this.existingContactDetail.applicationContactId;
      }
      this.dialogRef.close(newContactDetail);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
